import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ethers } from 'ethers';
import HoneyFarmQueen from '../../helpers/HoneyFarmQueenContract';
import BearsContract from '../../helpers/BearsContract';
import fetchData from '../../helpers/fetchMetadata';
import { owners } from '../../assets/Data/old_owners';
import config from '../../config';

interface MigrateBearProps {
  signer?: ethers.Signer;
  provider?: ethers.providers.Provider;
  address?: string;
}

interface OwnerMapping {
  old_id: string;
  new_id: number;
  owner: string;
}

interface MigratableBear {
  osId: string;
  newId: number;
  image: string;
  name: string;
  alreadyMinted: boolean;
}

// Load merkle data lazily only when needed for migration
let merkleDataCache: { id: number; leaf: string; proof: string[] }[] | null = null;
const loadMerkleData = async () => {
  if (merkleDataCache) return merkleDataCache;
  const res = await fetch('/data/prod.json');
  const data = await res.json();
  merkleDataCache = data.mappings || [];
  return merkleDataCache;
};

const MigrateBear: React.FC<MigrateBearProps> = ({ signer, provider, address }) => {
  const [migratableBears, setMigratableBears] = useState<MigratableBear[]>([]);
  const [selectedBear, setSelectedBear] = useState<MigratableBear | null>(null);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  // Filter owners snapshot by connected address - instant, no blockchain calls
  const userBears = useMemo(() => {
    if (!address) return [];
    const normalizedAddress = ethers.utils.getAddress(address);
    const filtered = (owners as OwnerMapping[]).filter(
      (o) => ethers.utils.getAddress(o.owner) === normalizedAddress
    );
    console.log(`[MigrateBear] Found ${filtered.length} bears for address ${normalizedAddress} out of ${(owners as OwnerMapping[]).length} total in snapshot`);
    return filtered;
  }, [address]);

  // Read provider - bypasses MetaMask for read operations
  const readProvider = useMemo(() => {
    return new ethers.providers.JsonRpcProvider(config.infuraUrl);
  }, []);

  // Write contract (needs signer for transactions)
  const honeyQueenContract = useMemo(() => {
    return signer ? new HoneyFarmQueen(signer) : null;
  }, [signer]);

  // Read contract (uses direct RPC to bypass MetaMask rate limits)
  const bearsContract = useMemo(() => {
    return new BearsContract(readProvider);
  }, [readProvider]);

  // Check which bears have already been migrated
  useEffect(() => {
    const checkMigrationStatus = async () => {
      if (userBears.length === 0) {
        setMigratableBears([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setMessage('Checking migration status...');

      try {
        const bears: MigratableBear[] = await Promise.all(
          userBears.map(async (bear) => {
            // Check if already minted on new contract
            let alreadyMinted = false;
            try {
              alreadyMinted = await bearsContract.exists(bear.new_id);
            } catch {
              // If exists() fails, try ownerOf
              try {
                await bearsContract.ownerOf(bear.new_id);
                alreadyMinted = true;
              } catch {
                alreadyMinted = false;
              }
            }

            // Fetch metadata
            let image = '';
            let name = `Bears Deluxe #${bear.new_id}`;
            try {
              const metadata = await fetchData(bear.new_id, 'bears');
              if (metadata?.data) {
                image = metadata.data.image || '';
                name = metadata.data.name || name;
              }
            } catch {
              // Ignore metadata errors
            }

            return {
              osId: bear.old_id,
              newId: bear.new_id,
              image,
              name,
              alreadyMinted,
            };
          })
        );

        setMigratableBears(bears);
        const unmigrated = bears.filter((b) => !b.alreadyMinted).length;
        setMessage(
          unmigrated > 0
            ? `Found ${unmigrated} bear(s) ready to migrate!`
            : bears.length > 0
            ? 'All your bears have been migrated!'
            : ''
        );
      } catch (err) {
        console.error('Error checking migration status:', err);
        setMessage('Error checking bears. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    checkMigrationStatus();
  }, [userBears, bearsContract]);

  // Handle migration
  const handleMigrate = useCallback(
    async (bear: MigratableBear) => {
      if (!honeyQueenContract || !signer) {
        setMessage('Please connect your wallet to migrate.');
        return;
      }

      if (bear.alreadyMinted) {
        setMessage('This bear has already been migrated!');
        return;
      }

      setProcessing(true);
      setMessage(`Loading merkle proof for ${bear.name}...`);

      try {
        // Load merkle data only when needed
        const merkleData = await loadMerkleData();
        const merkle = merkleData?.find((m) => m.id === bear.newId);

        if (!merkle) {
          setMessage('Error: Could not find merkle proof data for this bear.');
          setProcessing(false);
          return;
        }

        setMessage(`Migrating ${bear.name}...`);

        const tx = await honeyQueenContract.migrateBear(
          bear.osId,
          bear.newId,
          merkle.leaf,
          merkle.proof
        );

        setMessage('Transaction submitted. Waiting for confirmation...');
        await tx.wait();

        setMessage(`Successfully migrated ${bear.name}!`);

        // Update local state
        setMigratableBears((prev) =>
          prev.map((b) => (b.osId === bear.osId ? { ...b, alreadyMinted: true } : b))
        );
        setSelectedBear(null);
      } catch (err: any) {
        console.error('Migration error:', err);
        setMessage(`Migration failed: ${err.message || 'Unknown error'}`);
      } finally {
        setProcessing(false);
      }
    },
    [honeyQueenContract, signer]
  );

  if (!address) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-black bg-opacity-60 rounded-md border border-[#c4f6f9]">
        <h2 className="text-xl font-bold mb-4 text-[#c4f6f9]">Bear Migration</h2>
        <p className="text-gray-400 text-sm">Please connect your wallet to view and migrate your bears.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center p-4 md:p-8">
      <div className="w-full p-4 bg-black bg-opacity-60 text-white shadow-md rounded-md mb-6 border border-[#c4f6f9]">
        <h2 className="text-xl font-bold mb-2 text-[#c4f6f9]">Bear Migration</h2>
        <p className="text-gray-300 text-sm max-w-2xl">
          Migrate your Bears Deluxe from the OpenSea shared contract to the official Bears Deluxe
          contract. This unlocks Honey Farm features including honey claiming, hive minting, and more.
        </p>
      </div>

      {/* Status Message */}
      {message && (
        <div
          className={`mb-6 p-4 rounded-lg text-center max-w-md text-sm ${
            message.includes('Error') || message.includes('failed')
              ? 'bg-red-500/20 text-red-300 border border-red-500/50'
              : message.includes('Success')
              ? 'bg-green-500/20 text-green-300 border border-green-500/50'
              : 'bg-black bg-opacity-60 text-[#c4f6f9] border border-[#55bac3]'
          }`}
        >
          {message}
        </div>
      )}

      {/* Bears Grid */}
      {loading ? (
        <div className="flex items-center justify-center p-8">
          <div className="animate-pulse text-[#c4f6f9] text-sm">Loading bears...</div>
        </div>
      ) : migratableBears.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 w-full max-w-6xl">
          {migratableBears.map((bear) => (
            <div
              key={bear.osId}
              onClick={() => !bear.alreadyMinted && !processing && setSelectedBear(bear)}
              className={`relative flex flex-col items-center p-4 rounded-lg border-2 transition-all ${
                bear.alreadyMinted
                  ? 'border-gray-600 bg-black bg-opacity-40 cursor-not-allowed opacity-60'
                  : processing
                  ? 'border-gray-600 bg-black bg-opacity-60 cursor-not-allowed'
                  : selectedBear?.osId === bear.osId
                  ? 'border-[#ffa2f8] bg-black bg-opacity-60 cursor-pointer'
                  : 'border-[#55bac3] bg-black bg-opacity-60 hover:border-[#ffa2f8] cursor-pointer'
              }`}
            >
              {/* Bear Image */}
              <div className="w-full aspect-square mb-2 rounded overflow-hidden bg-gray-700">
                {bear.image ? (
                  <img src={bear.image} alt={bear.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-500">
                    No Image
                  </div>
                )}
              </div>

              {/* Bear Name */}
              <p className="text-white text-sm text-center truncate w-full">
                {bear.name}
              </p>

              {/* Status Badge */}
              {bear.alreadyMinted && (
                <span className="absolute top-2 right-2 px-2 py-1 bg-green-500 text-white text-xs rounded">
                  Migrated
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-gray-400 text-center p-8">
          <p className="text-sm">No bears found for this wallet.</p>
          <p className="text-xs mt-2">
            If you recently acquired bears on OpenSea, they may not be in the migration snapshot.
          </p>
        </div>
      )}

      {/* Selected Bear Action Panel */}
      {selectedBear && !selectedBear.alreadyMinted && (
        <div className="fixed bottom-0 left-0 right-0 bg-black bg-opacity-95 border-t border-[#55bac3] p-4 md:p-6">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {selectedBear.image && (
                <img
                  src={selectedBear.image}
                  alt={selectedBear.name}
                  className="w-16 h-16 rounded object-cover border border-[#c4f6f9]"
                />
              )}
              <div>
                <p className="text-white text-sm">{selectedBear.name}</p>
                <p className="text-[#55bac3] text-xs">Ready to migrate</p>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setSelectedBear(null)}
                disabled={processing}
                className="px-4 py-2 border-2 border-gray-600 hover:border-gray-500 rounded-md text-gray-300 text-sm transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleMigrate(selectedBear)}
                disabled={processing}
                className="px-6 py-2 bg-transparent border-2 border-[#ffa2f8] text-[#ffa2f8] hover:bg-[#ffa2f8] hover:text-black disabled:border-gray-600 disabled:text-gray-500 rounded-md text-sm transition-colors"
              >
                {processing ? 'Migrating...' : 'Migrate Bear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processing Overlay */}
      {processing && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center">
          <div className="bg-black bg-opacity-90 border-2 border-[#ffa2f8] p-8 rounded-lg text-white text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffa2f8] mx-auto mb-4"></div>
            <div className="text-[#ffa2f8]">{message || 'Processing transaction...'}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MigrateBear;
